<template>
  <div class="chatbox-container" v-show="value">
    <el-card class="chat-card">
      <div slot="header" class="clearfix">
        <el-button type="text" @click="clearMessages" class="clear-btn">
          <i style="font-size: 25px" class="el-icon-delete"/>
        </el-button>

        <span class="title">{{ aiName }}</span>

        <el-button type="text" @click="closeChatBox" class="close-btn">
          <i style="font-size: 25px" class="el-icon-close"/>
        </el-button>
      </div>

      <div class="message-list" ref="messageList">
        <div v-for="(msg) in messages" :key="msg.id" class="message-item" :class="msg.type">
          <div class="message-avatar" :class="{ 'sent-avatar': msg.type === 'sent' }">
            <img v-if="msg.msgType === 'assistant' || msg.msgType === 'system'" src="../../assets/layoutHome/AI-robot-avatar.png" alt="Avatar"/>
            <!--            <img v-else src="@/assets/about/one.png" alt="Avatar"/>-->
          </div>
          <div class="message-content">
            <div v-if="hasThinkContent(msg.content)" class="think-container" :data-uuid="msg.id" :class="{ expanded: isThinkExpanded(msg.id) }">
              <div class="think-header" @click="toggleThink">
                <span class="think-icon">▸</span>
                <span class="think-title">思考过程</span>
              </div>
              <VueMarkdown :key="msg.id" :source="parseThinkMarkdown(msg.content)" class="think-content"/>
            </div>
            <VueMarkdown :key="msg.id" :source="parseMarkdown(msg.content)"/>
            <!--            <div v-html="parseMarkdown(msg.content)"></div>-->
            <span class="timestamp">{{ formatTime(msg.timestamp) }}</span>
          </div>
        </div>
        <div class="message-item received" v-if="showLoading">
          <div class="message-avatar">
            <img src="../../assets/layoutHome/AI-robot-avatar.png" alt="Avatar"/>
          </div>
          <div class="message-content">
            <VueMarkdown key="114514" :source="parseMarkdown('思考中~')"/>
          </div>
        </div>
      </div>

      <div class="message-input" @click.stop>
        <el-input
            v-model="message"
            placeholder="输入消息"
            type="textarea"
            @keydown.native="handleKeydown"
            @click.native.stop
            clearable
            class="input-box"
            rows="2"
            resize="none"
            :autosize="{ minRows: 1, maxRows: 2 }"
        />
        <!-- 停止按钮 -->
        <el-button
            v-show="sendingMessage || manuallyStopped"
            type="text"
            @click="stopReceiving"
            class="stop-btn"
            :class="{ 'disabled': !sendingMessage }"
        >
          <i style="font-size: 24px" class="el-icon-video-pause"/>
        </el-button>
        <el-button
            v-show="!(sendingMessage || manuallyStopped)"
            type="primary"
            @click.native.stop="sendMessage"
            :disabled="sendingMessage || manuallyStopped"
            class="send-btn"
        >发送</el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import MarkdownIt from "markdown-it";
import markdownItKatex from "@vscode/markdown-it-katex";
import markdownItContainer from "markdown-it-container";
import markdownItHighlightjs from "markdown-it-highlightjs";
import VueMarkdown from "vue-markdown";
import 'highlight.js/styles/github.css';
import 'katex/dist/katex.min.css';
import {mapGetters} from "vuex";

const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true
})
    .use(markdownItHighlightjs, {
      auto: true,
      wrap: true,
    })
    .use(markdownItKatex,
        {
          throwOnError: false
        });

export default {
  name: "ChatBox",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    VueMarkdown
  },
  data() {
    return {
      aiName: sessionStorage.getItem('loginRoleName') === '学生' ? '小鼓老师' : '小鼓助教',
      sendingMessage: false,
      socketOpened: false,
      message: '',
      messages: [],
      socket: null,
      debounceTimer: null,
      thinkStates: new Map(),
      maxContextLength: 10,
      isUserScrolling: false,
      // 新增状态
      manuallyStopped: false,    // 手动停止状态
      reconnectDelay: 50,      // 当前重连延迟
      maxReconnectDelay: 50,  // 最大重连间隔30秒
      showLoading: false,
    };
  },
  computed: {
    ...mapGetters(["userName"]),
  },
  watch: {},
  mounted() {
    this.connectWebSocket();
    const messageList = this.$refs.messageList;
    messageList.addEventListener('scroll', this.handleScroll);

    this.messages.push({
      id: Date.now() + Math.random(),
      type: 'received',
      msgType: 'system',
      role: 'system',
      content: "您好！我是" + this.aiName + "。",
      timestamp: Date.now(),
    });
  },
  methods: {
    isThinkExpanded(uuid) {
      return this.thinkStates.get(uuid) ?? true; // 默认展开
    },
    handleKeydown(event) {
      if (event.key === 'Enter' && !event.altKey) {
        event.preventDefault();
        this.sendMessage();
      }
      // 如果按下的是 Alt + Enter，则插入换行
      if (event.key === 'Enter' && event.altKey) {
        this.insertLineBreak();
      }
    },
    toggleThink(event) {
      const header = event.target.closest('.think-header');
      if (!header) return;

      const container = header.closest('.think-container');
      if (!container) return;

      const uuid = container.dataset.uuid;
      const isExpanded = this.thinkStates.get(uuid) ?? true;

      // Toggle the state
      this.thinkStates.set(uuid, !isExpanded);
      // Update the class and handle height dynamically
      this.$nextTick(() => {
        const content = container.querySelector('.think-content');
        const contentHeight = content.scrollHeight;
        container.style.setProperty('--content-height', `${contentHeight}px`);
        container.classList.toggle('expanded', !isExpanded);
      });
    },
    handleMessage(event) {
      if (event.data === '[DONE]') {
        this.sendingMessage = false;
        const lastReceivedMessage = this.messages
            .slice()
            .reverse()
            .find(msg => msg.type === 'received' && this.hasThinkContent(msg.content));
        if (lastReceivedMessage) {
          this.thinkStates.set(lastReceivedMessage.id, false);
        }
        return;
      }
      const msgData = JSON.parse(event.data);
      console.log("onMessage-arrive-handleMessage", msgData);
      if (msgData.choices.length <= 0) {
        return;
      }
      const chunk = msgData.choices[0];
      let currentMessage = this.messages.find(msg => msg.type === 'received' && msg.id === msgData.id);

      // 格式化内容
      let formatContent = chunk.delta.content;
      if (currentMessage) {
        currentMessage.content += formatContent;
      } else {
        currentMessage = {
          id: msgData.id,
          type: 'received',
          msgType: 'assistant',
          role: 'assistant',
          content: formatContent,
          timestamp: Date.now(),
        };
        this.messages.push(currentMessage);
        this.thinkStates.set(currentMessage.id, true);
      }

      this.scrollToBottom();
    },
    handleMessageAli(event) {
      if (!(event && event.data)) {
        return;
      }
      if (event.data === '[DONE]') {
        this.sendingMessage = false;
        this.showLoading = false;
        return;
      }

      const msgData = JSON.parse(event.data);
      console.log("onMessage-arrive-handleMessageAli", msgData)
      if (msgData.output.finish_reason === 'stop') {
        this.showLoading = false;

        let currentMessage = {
          id: msgData.id,
          type: 'received',
          msgType: 'assistant',
          role: 'assistant',
          content: msgData.output.text,
          timestamp: Date.now(),
        };
        this.messages.push(currentMessage);
        this.scrollToBottom();
      }else if (msgData.output.finish_reason === 'null') {
        this.showLoading = true;
      }
    },
    // 新增：停止接收消息
    stopReceiving() {
      if (!this.sendingMessage) return;

      this.manuallyStopped = true;
      this.sendingMessage = false;

      // 优雅关闭连接（使用自定义关闭码4000）
      if (this.socket) {
        this.socket.close(4000, 'User stopped receiving');
      }

      // 立即重新连接
      this.manuallyStopped = false; // 重置手动停止状态
      this.connectWebSocket();
    },

    // 增强版连接方法
    connectWebSocket() {
      const ai_url = process.env.VUE_APP_AI_SERVER_URL;
      // this.socket = new WebSocket('ws://' + ai_url + '/chatStream');
      this.socket = new WebSocket('wss://' + ai_url);

      this.socket.onopen = () => {
        this.socketOpened = true;
        this.manuallyStopped = false;
        this.reconnectDelay = 1000; // 重置重连间隔
        console.log('WebSocket连接成功');
      };

      this.socket.onmessage = (event) => {
        if (this.manuallyStopped) {
          console.log('已停止接收，忽略消息');
          return;
        }

        const whiteList = ['G0120210626']
        if (whiteList.includes(this.userName)) {
          // 特殊账号用其他方式处理
          this.handleMessageAli(event);
        }else {
          this.handleMessage(event);
        }
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket错误:', error);
        this.sendingMessage = false;
      };

      this.socket.onclose = (event) => {
        console.log('连接关闭:', event);
        this.socketOpened = false;
        this.sendingMessage = false;

        // 手动停止时不自动重连
        if (event.code === 4000) return;

        // 自动重连（无次数限制）
        this.reconnectWebSocket();
      };
    },

    // 智能重连方法
    reconnectWebSocket() {
      if (this.manuallyStopped) return;

      setTimeout(() => {
        console.log(`尝试重连（间隔${this.reconnectDelay}ms）`);
        this.connectWebSocket();

        // 指数退避（不超过最大间隔）
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
      }, this.reconnectDelay);
    },

    // 修改发送方法（自动恢复）
    sendMessage() {
      if (this.manuallyStopped) {
        // 自动恢复连接
        this.manuallyStopped = false;
        this.reconnectDelay = 1000;
        this.connectWebSocket();
      }

      if (this.message.trim() && !this.sendingMessage) {
        this.sendingMessage = true;
        let newMessage = {
          id: Date.now() + Math.random(),
          type: 'sent',
          msgType: 'user',
          role: 'user',
          content: this.message,
          timestamp: Date.now(),
        };

        this.messages.push(newMessage);

        // 过滤role=system的数据
        const filteredMessages = this.messages.filter(m => m.role !== 'system');
        const contextMessages = filteredMessages.slice(-this.maxContextLength);
        console.log("this.$store.state.userName",this.userName)
        // Send the trimmed context to the WebSocket
        this.socket.send(JSON.stringify({
          username: this.userName,
          model: "Baichuan2-Turbo",
          messages: contextMessages,
        }));

        this.message = '';
        this.scrollToBottom();

        // setTimeout(() => {
        //   this.sendingMessage = false;
        // }, 1000);
      }
    },
    clearMessages() {
      this.socket.close();
      this.messages = [];
    },
    // 用户滚动时触发
    handleScroll() {
      const messageList = this.$refs.messageList;
      const nearBottom = messageList.scrollHeight - messageList.scrollTop - messageList.clientHeight <= 50;

      // 如果用户滚动到接近底部，允许自动滚动
      this.isUserScrolling = !nearBottom;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const messageList = this.$refs.messageList;

        // 如果用户没有手动滚动，并且距离底部较远，才进行自动滚动
        if (!this.isUserScrolling) {
          messageList.scrollTop = messageList.scrollHeight;
        }
      });
    },
    formatTime(timestamp) {
      const date = new Date(timestamp);

      // 获取小时、分钟、秒数，并且补充 0
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${hours}:${minutes}:${seconds}`;
    },
    closeChatBox() {
      this.$emit('input', false);
    },
    insertLineBreak() {
      this.message += '\n';
    },
    escapeBrackets(text) {
      const pattern = /(```[\s\S]*?```|`.*?`)|\\\[([\s\S]*?[^\\])\\\]|\\\((.*?)\\\)/g;
      return text.replace(
          pattern,
          (match, codeBlock, squareBracket, roundBracket) => {
            if (codeBlock) {
              return codeBlock;
            } else if (squareBracket) {
              return `$$${squareBracket}$$`; // 闭区间 [a, b] -> $$  a, b  $$
            } else if (roundBracket) {
              return `$${roundBracket}$`; // 开区间 (a, b) -> $(a, b)$
            }
            return match;
          },
      );
    },
    // 提取思考内容
    splitThinkContent(text) {
      if (!text) return { thinkContent: '', remainingContent: '' };

      // 使用更严格的正则匹配
      const thinkPattern = /<think>([\s\S]*?)(?:<\/think>|$)/gi;
      const matches = [];
      let remaining = text;
      let match;

      while ((match = thinkPattern.exec(text)) !== null) {
        matches.push(match[1]);
        remaining = remaining.replace(match[0], '');
      }

      return {
        thinkContent: matches.join('\n'),
        remainingContent: remaining.trim()
      };
    },
    hasThinkContent(content) {
      const { thinkContent } = this.splitThinkContent(content);
      // 移除标签后检查有效内容
      const strippedContent = thinkContent
          .replace(/<think>|<\/think>/g, '')
          .trim();
      return strippedContent.length > 0;
    },
    parseThinkMarkdown(text) {
      let splitText = this.splitThinkContent(text).thinkContent;
      const processedText = this.escapeBrackets(splitText);
      return md.render(processedText);
    },
    parseMarkdown(text) {
      let splitText = this.splitThinkContent(text).remainingContent;
      const processedText = this.escapeBrackets(splitText);
      return md.render(processedText);
    },
  },
  beforeDestroy() {
    const messageList = this.$refs.messageList;
    messageList.removeEventListener('scroll', this.handleScroll);
    if (this.socket) {
      this.socket.close(1000, 'Component destroyed');
    }
  }
};
</script>

<style scoped>

.chatbox-container {
  width: 50%;
  position: fixed;
  bottom: 5% !important;
  right: 20px;
  background-color: #e9f1f7; /* 浅蓝色背景 */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
}

.chat-card {
  background-color: #ffffff; /* 白色背景 */
  color: #333;
  border-radius: 12px;
}

.clearfix {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 12px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.clear-btn {
  position: absolute;
  left: 12px;
  padding: 6px 10px;
  font-size: 16px;
  color: #999999;
  cursor: pointer;
  transition: color 0.3s;
}

.message-list {
  display: flex;
  flex-direction: column;
  height: 50vh;
  line-height: 2;
  flex-grow: 1;
  overflow-y: auto;
  padding: 10px;
}

.message-item {
  display: flex;
  margin-bottom: 12px;
  padding: 12px;
  border-radius: 12px;
  word-wrap: break-word;
}

.sent {
  background-color: #b3e5fc; /* 浅蓝色用户消息背景 */
  text-align: right;
  align-self: flex-end;

  .message-content {
    align-items: flex-start;
    text-align: left;
  }

  .timestamp {
    text-align: left;
  }
}

.received {
  background-color: #f1f8ff; /* 明亮的灰白色AI消息背景 */
  text-align: left;
  align-self: flex-start;
}

.sent-avatar {
  order: 1;

  img {
    margin-left: 10px;
    margin-right: 0;
  }
}

.message-avatar img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 10px;
}

.message-content {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
}

.timestamp {
  font-size: 0.75em;
  color: #888;
  margin-top: 5px;
  text-align: right;
}

.message-input {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 12px;
  background-color: #f7faff;
  gap: 12px;
}

.el-input {
  flex-grow: 1;
  border-radius: 12px;
  background-color: #e3f2fd; /* 输入框的浅蓝色背景 */
  color: #333;
}

.el-button {
  border-radius: 12px;
  padding: 8px 16px;
}

.el-button:disabled {
  background-color: #b0bec5;
  color: #eee;
}

.title {
  flex-grow: 1;
  font-size: 22px;
  text-align: center;
}

.close-btn {
  position: absolute;
  right: 12px;
}

.close-btn:hover {
  color: red;
}

.chatbox-container {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-item {
  opacity: 0;
  animation: fadeInMessage 0.5s ease-in-out forwards;
}

@keyframes fadeInMessage {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .chatbox-container {
    width: 90%;
    max-width: 400px;
    height: 450px;
  }
}

@media (max-width: 480px) {
  .message-input {
    flex-direction: column;
  }

  .el-button {
    margin-top: 10px;
  }
}

.think-container {
  color: #8b8b8b;
  margin: 0;
  margin-bottom: 1rem;
  position: relative;
  transition: all 0.3s ease-in-out;

  .think-container {
    padding-bottom: 10px;
  }

  .think-header {
    user-select: none;
    border-radius: 10px;
    display: flex;
    align-items: center;
    width: fit-content;
    padding: 12px;
    cursor: pointer;
    background: #f5f5f5;
    transition: background 0.2s;
  }

  .think-header:hover {
    background: #eee;
  }

  .think-icon {
    transition: transform 0.2s;
    display: inline-block;
  }

  .think-container.expanded .think-icon {
    transform: rotate(90deg);
  }

  .think-content {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    transition: max-height 0.3s ease-in-out, opacity 0.2s ease-in-out 0.1s;
    padding: 8px 0 0 20px;
    color: #8b8b8b;
    position: relative;
    line-height: 1.6;
  }

  .think-content::before {
    content: "";
    position: absolute;
    left: 0;
    top: 10px;
    bottom: 0;
    border-left: 2px solid #e5e5e5;
  }

  /* Markdown 内容样式适配 */
  .think-content :deep() p {
    margin: 4px 0;
  }

  .think-content :deep() code {
    background-color: #f3f3f3;
    padding: 2px 4px;
    border-radius: 4px;
  }

  &.expanded {
    .think-content {
      max-height: var(--content-height); /* 展开时使用动态高度 */
      opacity: 1;
    }

    .think-icon {
      transform: rotate(90deg);
    }
  }
}

/* 新增停止按钮样式 */
.stop-btn {
  color: #ff4d4f;
  transition: all 0.3s;
}

.stop-btn:hover:not(.disabled) {
  color: #ff7875;
  transform: scale(1.1);
}

.stop-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 调整发送按钮禁用样式 */
.send-btn:disabled {
  background-color: #c0c4cc;
  border-color: #c0c4cc;
  color: #fff;
}
</style>